import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://www.greyfielddata.com';

export function SiteStructuredData() {
  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${SITE_URL}/#organization`,
        name: 'Greyfield Data',
        url: SITE_URL,
        logo: `${SITE_URL}/greyfield_data_white_logo-removebg-preview.png`,
        sameAs: ['https://www.linkedin.com/company/greyfield-data'],
      },
      {
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        name: 'Greyfield Data',
        url: SITE_URL,
        publisher: { '@id': `${SITE_URL}/#organization` },
      },
    ],
  };

  return <Helmet><script type="application/ld+json">{JSON.stringify(structuredData)}</script></Helmet>;
}

interface BreadcrumbItem {
  name: string;
  url: string;
}

export function BreadcrumbStructuredData({ items }: { items: BreadcrumbItem[] }) {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };

  return <Helmet><script type="application/ld+json">{JSON.stringify(structuredData)}</script></Helmet>;
}

interface ServiceStructuredDataProps {
  name: string;
  description: string;
  url: string;
}

export function ServiceStructuredData({ name, description, url }: ServiceStructuredDataProps) {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name,
    description,
    url,
    provider: {
      '@type': 'Organization',
      '@id': `${SITE_URL}/#organization`,
      name: 'Greyfield Data',
      url: SITE_URL,
    },
    areaServed: 'US',
  };

  return <Helmet><script type="application/ld+json">{JSON.stringify(structuredData)}</script></Helmet>;
}

interface ArticleStructuredDataProps {
  headline: string;
  description: string;
  url: string;
  datePublished: string;
  dateModified?: string;
  image?: string;
  author?: {
    name: string;
    url: string;
  };
}

export function ArticleStructuredData({
  headline,
  description,
  url,
  datePublished,
  dateModified,
  image = 'https://www.greyfielddata.com/dashboard.jpg',
  author = {
    name: 'Linus Tse',
    url: 'https://www.linkedin.com/in/linustse/',
  },
}: ArticleStructuredDataProps) {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline,
    description,
    image,
    datePublished,
    dateModified: dateModified || datePublished,
    author: {
      '@type': 'Person',
      name: author.name,
      url: author.url,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Greyfield Data',
      url: 'https://www.greyfielddata.com',
      logo: {
        '@type': 'ImageObject',
        url: 'https://www.greyfielddata.com/greyfield_data_white_logo-removebg-preview.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url,
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
    </Helmet>
  );
}

export function PersonStructuredData() {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: 'Linus Tse',
    url: 'https://www.greyfielddata.com',
    sameAs: [
      'https://www.linkedin.com/in/linustse/',
      'https://linustse.com',
    ],
    jobTitle: 'Data Engineer',
    worksFor: {
      '@type': 'Organization',
      name: 'Greyfield Data',
      url: 'https://www.greyfielddata.com',
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
    </Helmet>
  );
}
